import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel, DialogTitle, Description } from '@headlessui/react';
import SharedButton from '@/global/Button/Button';
import { getDefaultEmailLists, getEmailLists } from '@/services/emailLists';
import { isBookDemoBlockedEmailWithException, validateContactEmail } from '../Support/ValidationLogic';

const MODAL_Z = 100001;

type BookDemoEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onProceed: (email: string) => void;
};

export default function BookDemoEmailModal({ isOpen, onClose, onProceed }: BookDemoEmailModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setEmailError('');
      setIsBlocked(false);
      setIsChecking(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleContinue = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setEmailError('Please enter your email address.');
      return;
    }

    if (!validateContactEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    setIsChecking(true);
    setEmailError('');

    try {
      let lists = getDefaultEmailLists();
      try {
        lists = await getEmailLists(true);
      } catch {
        // Usar fallback local si la API no responde
      }

      if (await isBookDemoBlockedEmailWithException(trimmedEmail.toLowerCase(), lists)) {
        setIsBlocked(true);
        return;
      }

      onProceed(trimmedEmail);
    } catch {
      setEmailError('Could not verify your email. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  if (!isOpen || !mounted || typeof document === 'undefined') return null;

  const modalContent = isBlocked ? (
    <>
      <DialogTitle className="font-bold text-xl">Demo not available for academic users</DialogTitle>
      <Description className="text-gray-600 mt-2">
        Demos are only available for commercial users. If you have questions or would like to learn more,
        please contact us through our contact form.
      </Description>
      <div className="flex justify-end gap-2 pt-6">
        <SharedButton
          variant="gradient"
          onClick={() => {
            handleClose();
            navigate('/Support');
          }}
        >
          Go to contact form
        </SharedButton>
        <SharedButton variant="secondary" onClick={handleClose}>
          Close
        </SharedButton>
      </div>
    </>
  ) : (
    <>
      <DialogTitle className="font-bold text-xl">Book a Demo</DialogTitle>
      <Description className="text-gray-600 mt-2">
        Enter your work email to schedule a personalized demo with our team.
      </Description>
      <div className="mt-4">
        <label htmlFor="book-demo-email" className="form-label font-medium">
          Email
        </label>
        <input
          id="book-demo-email"
          type="email"
          className="form-control"
          value={email}
          onChange={event => {
            setEmail(event.target.value);
            if (emailError) setEmailError('');
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleContinue();
            }
          }}
          placeholder="you@company.com"
          autoComplete="email"
          disabled={isChecking}
        />
        {emailError && <small className="text-danger d-block mt-2">{emailError}</small>}
      </div>
      <div className="flex justify-end gap-2 pt-6">
        <SharedButton variant="gradient" onClick={() => void handleContinue()} disabled={isChecking}>
          {isChecking ? 'Checking...' : 'Continue'}
        </SharedButton>
        <SharedButton variant="secondary" onClick={handleClose} disabled={isChecking}>
          Cancel
        </SharedButton>
      </div>
    </>
  );

  return createPortal(
    <Dialog open={isOpen} onClose={handleClose} style={{ position: 'relative', zIndex: MODAL_Z }}>
      <div
        className="fixed inset-0 flex w-screen items-center justify-center p-4"
        style={{ zIndex: MODAL_Z, background: 'rgba(0, 0, 0, 0.35)' }}
      >
        <DialogPanel
          className="w-full max-w-lg overflow-y-auto space-y-2 border bg-white p-8 rounded-md shadow-xl"
          style={{ maxHeight: '80vh' }}
        >
          {modalContent}
        </DialogPanel>
      </div>
    </Dialog>,
    document.body,
  );
}
